import { startTransition, useCallback, useRef } from 'react';
import { useNavigate, useMatch } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useRecoilCallback } from 'recoil';
import { GET_WORKLIST_ITEM, GET_LAST_VIEWED_WORKLIST_ITEM_SMIDS } from 'modules/Apollo/queries';
import type {
  GetWorklistItemQuery,
  GetWorklistItemQueryVariables,
  GetLastViewedWorklistItemSmidsQuery,
  GetLastViewedWorklistItemSmidsQueryVariables,
} from 'generated/graphql';
import { AppRoutePath, NOT_FOUND_CASE_ID } from 'config/constants';
import { useAsyncGlobalAction } from 'hooks/useGlobalAction';
import { useOpenTabs } from './useOpenTabs';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import usePrevious from 'react-use/esm/usePrevious';
import { useUrlBuilder } from './useUrlBuilder';
import { activePriorStudiesState } from './useActivePriorStudies';
import { clearProviderMap } from 'modules/viewer/workers/PixelDataSharedWorker';
import { saveWindowSyncInfo } from 'modules/windowDesyncDetector';
import { logger } from 'modules/logger';
import { useStudies } from '../domains/viewer/Viewer/StudyLoader/useStudies';
import { getWARDCaseId } from 'domains/viewer/Viewer/Ward/utils';
import { WithApolloContext } from 'modules/Apollo/types';
import { useViewerMatch } from 'utils/viewerUrlUtils';
import { useReporterMatch } from './useReporterMatch';

type HookResult = {
  currentCaseId: Nullable<string>;
  /** Current case/worklist item in context */
  currentCase: Nullable<GetWorklistItemQuery['worklistItem']>;
  /** Refetches the case globally across all windows */
  refreshCase: () => Promise<void>;
  /** Loading state for case */
  loadingCase: boolean;
};

export const REPORTER_RELOAD_DELAY_MS = 1_000;

// module-level ref so that useCurrentComparativeStudies and useSetCurrentCase can share a timeout
let lastReporterTimeout: Nullable<ReturnType<typeof setTimeout>> = null;

export const useCurrentComparativeStudies = ({
  context,
}: WithApolloContext): [
  ReadonlyArray<string>,
  (arg1: { caseSmid?: string; studySmids: ReadonlyArray<string> }) => unknown,
] => {
  const currentCaseId = useCurrentCaseId();
  const { priorStudiesIds, autoloadedStudiesIds } = useStudies({ context });

  const buildUrl = useUrlBuilder();
  const navigate = useNavigate();
  const previousCaseId = usePrevious(currentCaseId);
  const [shouldRefreshViewer] = useFeatureFlagEnabled(FF.refresh_viewer_on_new_case);
  const [shouldRefreshReporter] = useFeatureFlagEnabled(FF.REFRESH_REPORTER_ON_NEW_CASE);
  const matchViewer = useMatch(AppRoutePath.VIEWER);
  const matchReporter = useMatch(AppRoutePath.REPORTER);

  const setActivePriorsForCase = useRecoilCallback(
    ({ set }) =>
      (
        caseSmid: string,
        studySmids: ReadonlyArray<string>,
        autoloadedSmids: ReadonlyArray<string>
      ) => {
        // Mirrors the URL filtering done by `useUrlBuilder.buildUrl`: the
        // `prior_studies` URL param holds only manually-added priors (i.e.
        // smids the user added that the AI did NOT auto-load), and
        // `skip_studies` holds autoloaded smids the user explicitly removed.
        //
        // Callers (e.g. StudiesBar `loadLazyStudies`) construct `studySmids`
        // by concatenating the current `priorStudiesIds` (which itself
        // concatenates `autoloadedPriors` and `studiesBySmid` from the
        // GraphQL response) with new clicks; that can introduce duplicates
        // when a smid appears in both server lists or when clicks race the
        // refetch. Dedupe here so the URL stays stable.
        const autoSet = new Set(autoloadedSmids);
        const studySet = new Set(studySmids);
        const priors = Array.from(studySet).filter((smid) => !autoSet.has(smid));
        const excluded = Array.from(autoSet).filter((smid) => !studySet.has(smid));
        set(activePriorStudiesState(caseSmid), { priors, excluded });
      },
    []
  );

  const setCurrentComparativeStudies = useCallback(
    ({
      caseSmid = currentCaseId,
      studySmids,
    }: {
      caseSmid?: Nullable<string>;
      studySmids: ReadonlyArray<string>;
    }) => {
      // VX-4394: bail out for a missing or NOT_FOUND case. With CLEAR_PATIENT_CONTEXT on (certain prod
      // customers) `useCurrentCaseId` can resolve to `NOT_FOUND_CASE_ID`, for which priors make no sense.
      if (caseSmid == null || caseSmid === NOT_FOUND_CASE_ID) return;

      const isSameCase = previousCaseId === caseSmid;

      if (isSameCase) {
        // Same case: update the Recoil source-of-truth. The URL is mirrored
        // by `ActivePriorStudiesUrlSync` via `history.replaceState` (silent —
        // does not notify react-router), avoiding the render storm that the
        // old `navigate(...)` path triggered. This atom-based path structurally
        // removes the same-case navigate loop VX-4394 previously had to guard.
        setActivePriorsForCase(caseSmid, studySmids, autoloadedStudiesIds);
        return;
      }

      // Cross-case navigation still goes through the URL builder. The target
      // case's atom hydrates from the URL on mount of the new viewer.
      // VX-4394: dedupe priors. `buildUrl` does not dedupe, and the case-sync callers concat the
      // clicked study onto the priors already in the URL, so a repeated lazy-load would otherwise grow
      // the `prior_studies` param (p1 → p1,p1 → …).
      const uniqueStudySmids = [...new Set(studySmids)];
      const builtUrl = buildUrl({
        caseSmid,
        studySmids: uniqueStudySmids,
        autoloadedStudySmids: [...new Set(autoloadedStudiesIds)],
      });

      if (builtUrl == null) return;

      // Pre-populate the target case's atom so any window already showing
      // that case picks up the new priors via BroadcastChannel without
      // waiting for a navigation event.
      setActivePriorsForCase(caseSmid, studySmids, autoloadedStudiesIds);

      if (shouldRefreshViewer && matchViewer) {
        clearProviderMap();
        window.location.href = builtUrl;
      } else if (shouldRefreshReporter && matchReporter) {
        if (lastReporterTimeout != null) {
          clearTimeout(lastReporterTimeout);
        }

        // delay hard-navigation to allow time to dispatch openCaseEvent to Extension
        lastReporterTimeout = setTimeout(() => {
          window.location.href = builtUrl;
        }, REPORTER_RELOAD_DELAY_MS);
      } else if (window.location.pathname !== builtUrl) {
        clearProviderMap();
        startTransition(() => {
          navigate(builtUrl);
        });
      }
    },
    [
      currentCaseId,
      buildUrl,
      autoloadedStudiesIds,
      previousCaseId,
      navigate,
      shouldRefreshViewer,
      shouldRefreshReporter,
      matchViewer,
      matchReporter,
      setActivePriorsForCase,
    ]
  );

  return [priorStudiesIds, setCurrentComparativeStudies];
};

export const useCurrentCaseId = (): Nullable<string> => {
  const [isClearPatientContextEnabled] = useFeatureFlagEnabled(FF.CLEAR_PATIENT_CONTEXT);

  // Highest priority as it has less chances of being stale
  const viewerUrlWorklistId = useMatch(AppRoutePath.VIEWER)?.params?.worklistId;
  const reporterUrlWorklistId = useMatch({ path: AppRoutePath.REPORTER, end: false })?.params
    ?.worklistId;
  const patientJacketUrlWorklistId = useMatch(AppRoutePath.PATIENT_JACKET)?.params?.smid;
  const patientJacketUndockedUrlWorklistId = useMatch(AppRoutePath.PATIENT_JACKET_UNDOCKED)?.params
    ?.smid;
  const wardWorklistId = getWARDCaseId();

  // Right after the url, as it may have a small delay and result stale
  const { tabs } = useOpenTabs();
  const worklistIds = tabs.map((tab) => tab.worklistIds).flat(1);
  const currentCaseID =
    viewerUrlWorklistId ??
    reporterUrlWorklistId ??
    patientJacketUrlWorklistId ??
    patientJacketUndockedUrlWorklistId ??
    wardWorklistId ??
    worklistIds[0];

  const { data } = useQuery<
    GetLastViewedWorklistItemSmidsQuery,
    GetLastViewedWorklistItemSmidsQueryVariables
  >(GET_LAST_VIEWED_WORKLIST_ITEM_SMIDS, {
    skip: currentCaseID != null || isClearPatientContextEnabled,
    context: { telemetry: { component: 'useCurrentCaseId' } },
  });

  if (isClearPatientContextEnabled) {
    return currentCaseID ?? NOT_FOUND_CASE_ID;
  }

  if (currentCaseID == null) {
    const lastViewedWorklistItemSmids = data?.me?.lastViewedWorklistItemSmids ?? [];
    return lastViewedWorklistItemSmids.length > 0 ? lastViewedWorklistItemSmids[0] : null;
  }

  return currentCaseID;
};

/** Builds and navigates to the URL with the caseId */
export const useSetCurrentCase = (): ((smid: string) => unknown) => {
  const navigate = useNavigate();
  const buildUrl = useUrlBuilder({ isActive: true });
  const [shouldRefreshViewer] = useFeatureFlagEnabled(FF.refresh_viewer_on_new_case);
  const [shouldRefreshReporter] = useFeatureFlagEnabled(FF.REFRESH_REPORTER_ON_NEW_CASE);
  const matchViewer = useViewerMatch();
  const matchReporter = useReporterMatch();

  return useCallback(
    (caseSmid: string) => {
      if (caseSmid == null || caseSmid === NOT_FOUND_CASE_ID) return;
      const url = buildUrl({ caseSmid });
      if (url == null) return;
      if (window.location.pathname !== url) {
        clearProviderMap();
        if (shouldRefreshViewer && matchViewer) {
          saveWindowSyncInfo(caseSmid);
          window.location.href = url;
        } else if (shouldRefreshReporter && matchReporter) {
          saveWindowSyncInfo(caseSmid);

          if (lastReporterTimeout != null) {
            clearTimeout(lastReporterTimeout);
          }
          // delay hard-navigation to allow time to dispatch openCaseEvent to Extension
          lastReporterTimeout = setTimeout(() => {
            window.location.href = url;
          }, REPORTER_RELOAD_DELAY_MS);
        } else {
          navigate(url);
          saveWindowSyncInfo(caseSmid);
        }
      }
    },
    [buildUrl, matchViewer, matchReporter, navigate, shouldRefreshViewer, shouldRefreshReporter]
  );
};

export const useCurrentCase = (): HookResult => {
  const currentCaseId = useCurrentCaseId();
  const {
    data: caseData,
    loading: loadingCase,
    refetch,
  } = useQuery<GetWorklistItemQuery, GetWorklistItemQueryVariables>(GET_WORKLIST_ITEM, {
    variables: currentCaseId == null ? undefined : { smid: currentCaseId },
    skip: currentCaseId == null || currentCaseId === NOT_FOUND_CASE_ID,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    context: {
      telemetry: {
        component: 'useCurrentCase',
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
      },
    },
  });

  const refetchWorklistItem = useCallback(async () => {
    if (currentCaseId != null) {
      logger.info('[Current Case]: refetching case');

      await refetch({ smid: currentCaseId });
    }
  }, [currentCaseId, refetch]);

  const prevData = useRef(caseData);
  if (caseData?.worklistItem?.smid !== prevData.current?.worklistItem?.smid) {
    prevData.current = caseData;
  }
  const currentCase = (caseData || prevData.current)?.worklistItem;

  // callSelf: BroadcastChannel never delivers a tab's own message to itself, so without
  // this, refreshCase() only refetches in other tabs, never this one.
  const refreshCase = useAsyncGlobalAction('use-current-case', refetchWorklistItem, {
    callSelf: true,
  });

  return {
    currentCaseId,
    currentCase,
    refreshCase,
    loadingCase: loadingCase && currentCase?.smid !== currentCaseId,
  };
};
